import { Box, CircularProgress, Grow, styled, Typography } from "@mui/material";
import { forwardRef, LegacyRef, useEffect, useRef, useState } from "react";

export type ComponentProps = {
	setLoading: (loadState: boolean) => void;
};

export interface LoaderProps<T> {
	Component: React.ComponentType<T & ComponentProps>;
	message: String;
}
const PREFIX = "Loader";

const classes = {
	spinner: `${PREFIX}-spinner`,
	loaderDiv: `${PREFIX}-loaderDiv`,
};
const Root = styled("div")({
	[`& .${classes.spinner}`]: {
		backdropFilter: "blur(100px)",
		width: "100%",
		position: "absolute",
	},
	[`& .${classes.loaderDiv}`]: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
	},
});
export function Loader<T>(
	Component: React.ComponentType<T & ComponentProps>,
	message: String,
): React.FC<T> {
	const WrapperPage = (props: T): JSX.Element => {
		const [isLoading, setLoading] = useState(true);
		const [animationComplete, setAnimationComplete] = useState(false);
		const setLoadingState = (loadState: boolean): void => setLoading(loadState);
		const timerRef = useRef<number>();

		const CustomComponent = forwardRef(function CustomComponent(
			componentProps: {},
			ref: LegacyRef<HTMLDivElement>,
		) {
			return (
				<div ref={ref} {...componentProps} className={classes.loaderDiv}>
					<CircularProgress size="5rem" color="secondary" />
					<Typography variant="h2">{message}</Typography>
				</div>
			);
		});

		const Spinner = (
			<Grow in={isLoading || !animationComplete}>
				<CustomComponent />
			</Grow>
		);

		useEffect(() => {
			timerRef.current = window.setTimeout(() => {
				setAnimationComplete(true);
			}, 2000);

			return () => {
				clearTimeout(timerRef.current);
			};
		}, []);

		return (
			<Root>
				{(isLoading || !animationComplete) && (
					<Box
						height="100%"
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						zIndex={5}
						className={classes.spinner}
					>
						{Spinner}
					</Box>
				)}

				<Component {...props} setLoading={setLoadingState} />
			</Root>
		);
	};
	return WrapperPage;
}
